import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { ChevronLeft, Upload, Download, Trash2, FileText, Loader2, Save } from 'lucide-react';
import { toast } from 'sonner';

const NDT_METHOD_LABELS = {
  AE: 'AE (Acoustic Emission)', ET: 'ET (Eddy Current)', LT: 'LT (Leak Testing)',
  MT: 'MT (Magnetic Particle)', PCT: 'PCT (Pressure Change Testing)', PT: 'PT (Liquid Penetrant)',
  RT: 'RT (Radiographic)', RVI: 'RVI (Remote Visual Inspection)', UT: 'UT (Ultrasonic)',
  UTT: 'UTT (Ultrasonic Thickness)', VTG: 'VTG (Visual Testing General)', VTW: 'VTW (Visual Testing Welds)',
};
// VTG and VTW are tracked distinctly system-wide; show their canonical codes.
const NDT_METHOD_ACRONYMS = {
  AE: 'AE', ET: 'ET', LT: 'LT', MT: 'MT', PCT: 'PCT', PT: 'PT',
  RT: 'RT', RVI: 'RVI', UT: 'UT', UTT: 'UTT', VTG: 'VTG', VTW: 'VTW',
};
const LEVEL_OPTIONS = ['Trainee', 'Level I', 'Level II', 'Level III'];
const FREQUENCY_OPTIONS = [1, 2, 3, 5];
const STATUS_BADGES = {
  PENDING: 'bg-gray-100 text-gray-700',
  ACTIVE: 'bg-green-100 text-green-700',
  EXPIRING: 'bg-amber-100 text-amber-800',
  EXPIRED: 'bg-red-100 text-red-700',
  SUSPENDED: 'bg-yellow-100 text-yellow-700',
  REVOKED: 'bg-red-100 text-red-800',
};

const apiBase = process.env.REACT_APP_BACKEND_URL;

// Format a stored date string as MM-DD-YYYY without timezone shifts.
const formatMMDDYYYY = (value) => {
  if (!value) return '—';
  const dateOnly = String(value).split('T')[0];
  const m = dateOnly.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return dateOnly;
  return `${m[2]}-${m[3]}-${m[1]}`;
};

export default function MethodDetailPage() {
  const { recordId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const personnelIdFromUrl = searchParams.get('personnelId');
  const { api, user } = useAuth();
  const isAdmin = user?.role === 'ADMIN';

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [record, setRecord] = useState(null);
  // Phase 3 polish: Status is a single control. "AUTOMATIC" = computed by backend; any other value = admin override.
  const blankForm = {
    level: '', training_frequency_years: 3, certification_date: '',
    status: 'AUTOMATIC', current_trainee_hours: 0,
  };
  const [form, setForm] = useState(blankForm);
  const [pristine, setPristine] = useState(blankForm);  // snapshot of last-saved state for dirty detection
  const [files, setFiles] = useState([]);
  const [uploadTitle, setUploadTitle] = useState('');
  const [uploadFile, setUploadFile] = useState(null);
  const [deleteFileTarget, setDeleteFileTarget] = useState(null);

  const fetchRecord = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/lms/ndt-records/${recordId}`);
      setRecord(data);
      const override = data.manual_status_override;
      const next = {
        level: data.level || '',
        training_frequency_years: data.training_frequency_years || 3,
        certification_date: data.certification_date ? data.certification_date.split('T')[0] : '',
        status: override || 'AUTOMATIC',
        current_trainee_hours: data.current_trainee_hours || 0,
      };
      setForm(next);
      setPristine(next);
      const filesResp = await api.get(`/lms/ndt-records/${recordId}/files`);
      setFiles(filesResp.data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load method detail');
    } finally {
      setLoading(false);
    }
  }, [api, recordId]);

  useEffect(() => { fetchRecord(); }, [fetchRecord]);

  // Mirror the backend's leap-year handling: 02/29/2024 + 3y → 02/28/2027.
  // Build the date in UTC to dodge timezone shifts, then if the target date isn't valid
  // (Feb 29 in a non-leap year), fall back to Feb 28.
  const computeDueDate = (certDate, years) => {
    if (!certDate) return '';
    const m = String(certDate).split('T')[0].match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!m) return '';
    const y = Number(m[1]) + Number(years || 0);
    const month = Number(m[2]);
    const day = Number(m[3]);
    const candidate = new Date(Date.UTC(y, month - 1, day));
    if (candidate.getUTCMonth() + 1 !== month) {
      // JS rolled the date forward (e.g., Feb 29 → Mar 1). Snap to last day of prior month.
      const lastDay = new Date(Date.UTC(y, month, 0)).getUTCDate();
      return `${y}-${String(month).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
    }
    return `${y}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  };
  const previewDueDate = computeDueDate(form.certification_date, form.training_frequency_years);
  const isTrainee = form.level === 'Trainee';

  // Today's date as YYYY-MM-DD for the cert-date <input max=…> attribute.
  const today = new Date();
  const todayISO = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

  // Manual override is active when the current saved value isn't AUTOMATIC.
  const isOverridden = !!(record && record.manual_status_override);

  // Dirty detection — Save is enabled only when something the user can edit has changed.
  const isDirty = (
    form.level !== pristine.level ||
    Number(form.training_frequency_years) !== Number(pristine.training_frequency_years) ||
    form.certification_date !== pristine.certification_date ||
    (isAdmin && form.status !== pristine.status) ||
    (form.level === 'Trainee' && Number(form.current_trainee_hours) !== Number(pristine.current_trainee_hours))
  );

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        level: form.level || '',
        training_frequency_years: Number(form.training_frequency_years),
        certification_date: form.certification_date || null,
      };
      if (form.level === 'Trainee') {
        payload.current_trainee_hours = Number(form.current_trainee_hours) || 0;
      }
      if (isAdmin) {
        payload.status = form.status; // AUTOMATIC clears the override
      }
      await api.put(`/lms/ndt-records/${recordId}`, payload);
      toast.success('Method updated');
      await fetchRecord();  // re-snapshots pristine → returns Save to disabled state
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail
        : Array.isArray(detail) ? detail.map(d => d?.msg || JSON.stringify(d)).join('; ')
        : 'Failed to save';
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleUpload = async () => {
    if (!uploadFile || !uploadTitle.trim()) {
      toast.error('Title and PDF file are required');
      return;
    }
    if (uploadFile.type !== 'application/pdf') {
      toast.error('Only PDF files are allowed');
      return;
    }
    const fd = new FormData();
    fd.append('title', uploadTitle.trim());
    fd.append('file', uploadFile);
    try {
      await api.post(`/lms/ndt-records/${recordId}/files`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Record uploaded');
      setUploadTitle('');
      setUploadFile(null);
      document.getElementById('ndt-file-input').value = '';
      await fetchRecord();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    }
  };

  const confirmDeleteFile = async () => {
    if (!deleteFileTarget) return;
    try {
      await api.delete(`/lms/ndt-records/files/${deleteFileTarget.id}`);
      toast.success('Record removed');
      setDeleteFileTarget(null);
      await fetchRecord();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delete failed');
    }
  };

  if (loading || !record) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[50vh]">
        <Loader2 className="w-8 h-8 animate-spin text-[#005EF8]" />
      </div>
    );
  }

  const showTrainee = form.level === 'Trainee';

  return (
    <div className="p-6 max-w-5xl mx-auto" data-testid="method-detail-page">
      {/* Header: technician name + Tech ID · Facility subtitle (method moved into card) */}
      <div className="mb-6">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            const pid = personnelIdFromUrl || record.personnel.id;
            navigate(`/training/ndt?personnelId=${pid}`);
          }}
          className="mb-3"
          data-testid="back-to-records-btn"
        >
          <ChevronLeft className="w-4 h-4 mr-1" /> Back to NDT Methods List
        </Button>
        <h1 className="text-2xl font-bold text-gray-900">
          {record.personnel.first_name} {record.personnel.last_name}
        </h1>
        <p className="text-sm text-gray-500">
          {record.personnel.technician_id || (record.personnel.role === 'TECHNICIAN' ? '—' : 'N/A')}
          {record.facility_name ? ` · ${record.facility_name}` : ''}
        </p>
      </div>

      {/* Certification Details card — Save button attached at the bottom of the card */}
      <Card className="mb-6">
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Certification Details</CardTitle>
        </CardHeader>
        <CardContent className="pt-0 space-y-4">
          {/* Row 1: Method · Level · Frequency */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label className="text-xs">Method</Label>
              <p className="text-sm font-medium mt-2">{NDT_METHOD_ACRONYMS[record.method] || record.method}</p>
            </div>
            <div>
              <Label htmlFor="level-select" className="text-xs">Level</Label>
              <select
                id="level-select"
                value={form.level}
                onChange={(e) => setForm({ ...form, level: e.target.value })}
                className="w-full mt-1 h-9 px-3 border rounded-md bg-background text-sm"
                data-testid="method-level-select"
              >
                <option value="">—</option>
                {LEVEL_OPTIONS.map(l => <option key={l} value={l}>{l}</option>)}
              </select>
            </div>
            <div>
              <Label htmlFor="freq-select" className="text-xs">Frequency (years)</Label>
              <select
                id="freq-select"
                value={form.training_frequency_years}
                onChange={(e) => setForm({ ...form, training_frequency_years: Number(e.target.value) })}
                disabled={isTrainee}
                className="w-full mt-1 h-9 px-3 border rounded-md bg-background text-sm disabled:bg-gray-50 disabled:text-gray-400 disabled:cursor-not-allowed"
                data-testid="method-freq-select"
              >
                {FREQUENCY_OPTIONS.map(y => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>
          </div>

          {/* Row 2: Cert Date · Due Date · Status */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label htmlFor="cert-date" className="text-xs">Certification Date</Label>
              <Input
                id="cert-date"
                type="date"
                value={isTrainee ? '' : form.certification_date}
                max={todayISO}
                onChange={(e) => setForm({ ...form, certification_date: e.target.value })}
                disabled={isTrainee}
                className="mt-1 disabled:bg-gray-50 disabled:text-gray-400 disabled:cursor-not-allowed"
                data-testid="method-cert-date"
              />
              {isTrainee && (
                <p className="text-xs text-gray-500 mt-1">Not applicable while level is Trainee.</p>
              )}
            </div>
            <div>
              <Label className="text-xs">Due Date</Label>
              {isTrainee ? (
                <p className="text-sm font-medium mt-2" data-testid="method-due-date">—</p>
              ) : (
                <>
                  {/* Source of truth: the stored value from the backend. The frontend never */}
                  {/* "owns" this number — it just renders what was last persisted. */}
                  <p className="text-sm font-medium mt-2" data-testid="method-due-date">
                    {formatMMDDYYYY(record.due_date)}
                  </p>
                  {/* While the form is dirty AND the preview differs from the stored value, */}
                  {/* show the projection so users can see what Save will produce. */}
                  {isDirty && previewDueDate && previewDueDate !== (record.due_date ? String(record.due_date).split('T')[0] : '') && (
                    <p className="text-xs text-blue-600 mt-1" data-testid="method-due-date-preview">
                      → {formatMMDDYYYY(previewDueDate)} after save
                    </p>
                  )}
                </>
              )}
            </div>
            <div>
              <Label className="text-xs">Status</Label>
              {isAdmin ? (
                <select
                  value={form.status}
                  onChange={(e) => setForm({ ...form, status: e.target.value })}
                  className="w-full mt-1 h-9 px-3 border rounded-md bg-background text-sm"
                  data-testid="method-status-control"
                >
                  <option value="AUTOMATIC">Automatic</option>
                  <option value="ACTIVE">Active</option>
                  <option value="EXPIRING">Expiring</option>
                  <option value="EXPIRED">Expired</option>
                  <option value="PENDING">Pending</option>
                  <option value="SUSPENDED">Suspended</option>
                  <option value="REVOKED">Revoked</option>
                </select>
              ) : (
                <div className="mt-2">
                  <span
                    className={`text-xs px-2 py-1 rounded-full font-medium ${STATUS_BADGES[record.status] || 'bg-gray-100 text-gray-700'}`}
                    data-testid="method-status-badge"
                  >
                    {record.status}
                  </span>
                </div>
              )}
              {isOverridden && (
                <p className="text-xs text-amber-700 mt-1" data-testid="method-status-override-note">
                  This status is manually overridden.
                </p>
              )}
            </div>
          </div>

          {/* Trainee Hours sub-card — distinct visual block, only when Level=Trainee */}
          {showTrainee && (
            <div
              className="mt-2 rounded-md border border-amber-200 bg-amber-50/50 p-4"
              data-testid="trainee-hours-section"
            >
              <div className="text-sm font-semibold text-gray-900 mb-2">Trainee Hours</div>
              <div className="flex items-center gap-3">
                <Input
                  type="number"
                  min="0"
                  step="0.5"
                  value={form.current_trainee_hours}
                  onChange={(e) => setForm({ ...form, current_trainee_hours: e.target.value })}
                  className="w-28"
                  data-testid="trainee-current-hours"
                />
                <span className="text-sm text-gray-700">
                  / {record.target_hours != null ? (
                    <span className="font-medium">{record.target_hours} hrs</span>
                  ) : (
                    <span className="text-amber-700">No target set</span>
                  )}
                </span>
              </div>
              {record.target_hours == null && isAdmin && (
                <p className="text-xs text-gray-600 mt-2">
                  <Link to="/training/ndt/hours-targets" className="text-[#005EF8] hover:underline">
                    Set an hours target
                  </Link> for {NDT_METHOD_ACRONYMS[record.method] || record.method} in the Trainee Hours Targets page.
                </p>
              )}
            </div>
          )}

          {/* Save attached to the card */}
          <div className="flex justify-end pt-3 border-t">
            <Button
              onClick={handleSave}
              disabled={saving || !isDirty}
              data-testid="method-save-btn"
            >
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Save
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Records section */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <FileText className="w-4 h-4 text-[#005EF8]" />
            Records
            <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">
              {files.length}
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {/* Upload form */}
          <div className="flex flex-col md:flex-row gap-3 items-stretch md:items-end border-b pb-4 mb-4">
            <div className="flex-1">
              <Label className="text-xs">Title</Label>
              <Input
                value={uploadTitle}
                onChange={(e) => setUploadTitle(e.target.value)}
                placeholder="Training Hours"
                className="mt-1"
                data-testid="ndt-file-title"
              />
            </div>
            <div className="flex-1">
              <Label className="text-xs">PDF file</Label>
              <Input
                id="ndt-file-input"
                type="file"
                accept="application/pdf"
                onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
                className="mt-1"
                data-testid="ndt-file-input"
              />
            </div>
            <Button
              onClick={handleUpload}
              disabled={!uploadTitle.trim() || !uploadFile}
              data-testid="ndt-file-upload-btn"
            >
              <Upload className="w-4 h-4 mr-2" />
              Upload
            </Button>
          </div>

          {files.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-6">No records uploaded yet.</p>
          ) : (
            <div className="border rounded-lg overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Title</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Uploaded by</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Uploaded</th>
                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {files.map(f => (
                    <tr key={f.id} data-testid={`ndt-file-row-${f.id}`}>
                      <td className="px-4 py-2 text-sm font-medium">{f.title}</td>
                      <td className="px-4 py-2 text-sm text-gray-600">{f.uploaded_by_name || '—'}</td>
                      <td className="px-4 py-2 text-sm text-gray-600">
                        {new Date(f.uploaded_at).toLocaleString()}
                      </td>
                      <td className="px-4 py-2 text-right">
                        <div className="flex justify-end gap-2">
                          <a
                            href={f.file_url?.startsWith('http') ? f.file_url : `${apiBase}${f.file_url}`}
                            target="_blank"
                            rel="noreferrer"
                            className="text-sm text-[#005EF8] hover:underline inline-flex items-center"
                            data-testid={`ndt-file-view-${f.id}`}
                          >
                            View
                          </a>
                          <a
                            href={f.file_url?.startsWith('http') ? f.file_url : `${apiBase}${f.file_url}`}
                            download={f.filename}
                            className="text-sm text-gray-700 hover:underline inline-flex items-center"
                            data-testid={`ndt-file-download-${f.id}`}
                          >
                            <Download className="w-4 h-4 mr-1" />
                            Download
                          </a>
                          <button
                            type="button"
                            onClick={() => setDeleteFileTarget(f)}
                            className="text-sm text-red-600 hover:underline inline-flex items-center"
                            data-testid={`ndt-file-delete-${f.id}`}
                          >
                            <Trash2 className="w-4 h-4 mr-1" />
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Delete confirmation */}
      <AlertDialog open={!!deleteFileTarget} onOpenChange={(o) => { if (!o) setDeleteFileTarget(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this record?</AlertDialogTitle>
            <AlertDialogDescription className="text-gray-800">
              The PDF "{deleteFileTarget?.title}" will be hidden from this view. It stays on file for audit. Are you sure?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setDeleteFileTarget(null)} data-testid="ndt-file-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDeleteFile}
              className="bg-red-600 hover:bg-red-700"
              data-testid="ndt-file-delete-confirm"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
